import { PageWithTitle } from 'src/Common/components';
import styled from '@emotion/styled';
import { Box, InputAdornment, Stack, TextField, Typography } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import { useMemo, useState } from 'react';
import { LiveBadge } from 'src/Common/components/LiveBadge/LiveBadge';
import { ToggleUpdatesButton } from 'src/Common/components/ToggleUpdatesButton/ToggleUpdatesButton';
import { usePredictions } from '../context';
import { PredictionsTable } from '../components';

const PredictionsPageContent = () => {
  const { opportunities, isConnected, count } = usePredictions();
  const [searchQuery, setSearchQuery] = useState('');

  const filteredOpportunities = useMemo(() => {
    const normalizedQuery = searchQuery.trim().toLowerCase();
    if (!normalizedQuery) return opportunities;

    const queryTokens = normalizedQuery.split(/\s+/).filter(Boolean);

    return opportunities.filter((opportunity) => {
      const searchableParts = [
        opportunity.pairName,
        opportunity.direction?.yesTitle,
        opportunity.direction?.noTitle,
        ...(opportunity.keywordOverlap ?? [])
      ];

      const searchableText = searchableParts
        .filter(Boolean)
        .join(' ')
        .toLowerCase();

      return queryTokens.every((token) => searchableText.includes(token));
    });
  }, [opportunities, searchQuery]);

  return (
    <PageWithTitle>
      <Stack direction="row">
        <Box style={{ width: '100%' }}>
          <Toolbar>
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', lg: '1fr minmax(320px, 520px) 1fr' },
                alignItems: 'center',
                gap: 1.5
              }}
            >
              <Stack direction="row" spacing={2} alignItems="center" sx={{ justifySelf: 'start' }}>
                <Typography
                  variant="h6"
                  color="text.primary"
                  sx={{ whiteSpace: 'nowrap' }}
                >
                  Opportunities
                </Typography>
                {count > 0 && (
                  <Typography
                    sx={{
                      fontSize: '0.8rem',
                      color: 'grey.500',
                      whiteSpace: 'nowrap'
                    }}
                  >
                    ({filteredOpportunities.length}/{count} opportunities)
                  </Typography>
                )}
              </Stack>
              <TextField
                size="small"
                value={searchQuery}
                onChange={(event) => setSearchQuery(event.target.value)}
                placeholder="Search opportunities by pair keywords..."
                sx={{
                  width: '100%',
                  justifySelf: 'center'
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon fontSize="small" />
                    </InputAdornment>
                  )
                }}
              />
              <Stack direction="row" spacing={1} alignItems="center" sx={{ justifySelf: { xs: 'start', lg: 'end' } }}>
                <LiveBadge isLive={isConnected} />
                <ToggleUpdatesButton subsystem="predictions" />
              </Stack>
            </Box>
          </Toolbar>

          <PredictionsTable
            data={filteredOpportunities}
            isLoading={isConnected && opportunities.length === 0}
          />
        </Box>
      </Stack>
    </PageWithTitle>
  );
};

export const PredictionsPage = () => {
  return <PredictionsPageContent />;
};

const Toolbar = styled.div({
  display: 'flex',
  flexDirection: 'column',
  width: '100%'
});
